import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { useUserStore } from '../store/userStore';
import { Team, CreateTeamData, TeamApiData } from '../types';

// Хуки для команд
export const useTeams = () => {
  return useQuery({
    queryKey: ['teams'],
    queryFn: async (): Promise<Team[]> => {
      return await api('/teams');
    },
  });
};

export const useTeam = (id: string) => {
  return useQuery({
    queryKey: ['teams', id],
    queryFn: async (): Promise<Team> => {
      return await api(`/teams/${id}`);
    },
    enabled: !!id,
  });
};

// Хуки для мутаций команд
export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamData) => {
      return await api('/teams', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TeamApiData }) => {
      return await api(`/teams/${id}`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await api(`/teams/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

// Справочник команд для всех сотрудников: кто в какой команде и кто её ведёт.
// Отдельно от useTeams — тот роут админский и отдаёт служебные поля.
export const useTeamsOverview = () => {
  return useQuery({
    queryKey: ['teams', 'overview'],
    queryFn: async (): Promise<Team[]> => api('/teams/overview'),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Мои люди по оргграфу сервера (`GET /users/me/reports`): лид и PM команды, руководитель
 * отдела — те же «подчинённые», по которым сервер считает охват `reports`.
 */
export const useMyReportIds = (): Set<string> => {
  const userId = useUserStore((s) => s.user?._id);
  const { data } = useQuery({
    queryKey: ['users', 'me', 'reports', userId],
    queryFn: (): Promise<string[]> => api('/users/me/reports'),
    enabled: !!userId,
    staleTime: 60_000,
  });
  return useMemo(() => new Set(data ?? []), [data]);
};
